import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MerchandiseService } from '@epicuro-next/data/merchandise';
import { selectSessionState } from '@epicuro-next/state/session-state';
import { isEqual, isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import { transformStyleTagContents } from '../../utils';

@Component({
  selector: 'epc-content-slot',
  templateUrl: './content-slot.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContentSlotComponent implements OnInit, OnDestroy {
  @Input()
  public set slotName(value: string) {
    this.slotName$.next(value);
  }

  @ViewChild('host') public host?: ElementRef;

  public slotName$ = new BehaviorSubject<string | null>(null);
  public content$: Observable<string | null>;

  private destroy$$ = new Subject<void>();
  private contentParams$ = this.store.pipe(
    select(selectSessionState),
    map((state) => {
      const { siteId, subSiteId, accountId } = state;
      if (!siteId || !accountId) return undefined;
      return {
        siteId,
        subSiteId,
        accountId,
      };
    }),
    filter(isNonNullish),
  );

  constructor(
    private merchandiseService: MerchandiseService,
    private store: Store,
    private renderer: Renderer2,
  ) {}

  public ngOnInit() {
    this.setupContent();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  private setupContent() {
    combineLatest([
      this.slotName$.pipe(distinctUntilChanged()),
      this.contentParams$.pipe(distinctUntilChanged(isEqual)),
    ])
      .pipe(
        switchMap(([slotName, { siteId, subSiteId, accountId }]) =>
          slotName
            ? this.merchandiseService
                .getPulseContent(slotName, siteId, subSiteId, accountId)
                .pipe(
                  map((contentStr) => ({ slotName, contentStr })),
                  catchError(() => of(null)),
                )
            : of(null),
        ),
        takeUntil(this.destroy$$),
      )
      .subscribe((res) => {
        if (!res) this.clearContent();
        else {
          const { contentStr, slotName } = res;
          this.renderContent(contentStr, slotName);
        }
      });
  }

  private clearContent() {
    if (this.host) {
      this.renderer.setProperty(this.host.nativeElement, 'innerHTML', '');
    }
  }

  private renderContent(contentStr: string, hostId: string) {
    if (this.host) {
      const content = transformStyleTagContents(contentStr ?? '', `#${hostId}`);
      this.renderer.setProperty(this.host.nativeElement, 'innerHTML', content);
    }
  }
}
